import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import Cropper from 'cropperjs';
import tooltip from '@/mixins/components/tooltip';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';

export default Vue.component('dynamic-input-upload', {
  template: require('./template.html'),
  name: 'dynamic-input-upload',
  mixins: [guid, uploader],
  computed: {
    ...mapGetters(['currentSite', 'activeSiteType']),
    fileName() {
      return path.basename(this.activeItem[this.type]);
    },
  },
  components: {
    uploadProgress,
    tooltip,
  },
  props: [
    'active-item',
    'type',
    'active-input',
    'value',
    'inner-tabs',
    'dynamic-tab',
    'open-cropper',
    'on-create',
  ],
  data() {
    return {
      imgLoading: false,
      imageFiles: [],
      showFiles: false,
      tempPath: '',
      bucketPath: null,
      prefix: null,
      thumbnails: null,
      confirmDeleteImg: false,
      activeImage: null,
      hoverDel: false,
      activeIndex: null,
      cropper: null,
      localFile: null,
      activeAspect: {},
      restrictAspect: false,
      targetFile: null,
      targetName: '',
      aspectRatios: {
        raw: {
          display: 'Raw Image (Ignore Cropper)',
          ratio: 0,
        },
        free: {
          display: 'Free',
        },
        shortRect: {
          ratio: 4 / 5,
          height: 750,
          display: '4:5',
          width: 600,
        },
        tallRect: {
          ratio: 2 / 3,
          height: 750,
          display: '2:3',
          width: 500,
        },
        square: {
          ratio: 1 / 1,
          height: 600,
          display: '1:1',
          width: 600,
        },
        smallWideRect: {
          ratio: 4 / 3,
          display: '4:3',
          height: 600,
          width: 800,
        },
        wideRect: {
          ratio: 16 / 9,
          height: 700,
          display: '16:9',
          width: 1244,
        },
      },
    };
  },
  watch: {
    innerTabs: {
      handler(tab) {
        if (tab[this.dynamicTab]) {
          this.setInitialValues();
        }
        // always destroy cropper when tabs are changed
        this.destroyCropper();
      },
      deep: true,
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => this.setCropper(), 300);
      }
    },
  },
  created() {
    if (this.onCreate) {
      this.setInitialValues();
    }
  },
  methods: {
    setInitialValues() {
      // for s3 assets
      this.bucketPath = `_droplet-assets/${this.activeSiteType}/${this.type}/`;
      this.prefix = `drz_${this.activeSiteType}_${Date.now()}_`;
      this.thumbnails = `_droplet-assets/${this.activeSiteType}/${this.type}/_thumbnails/`;
      this.activeAspect = this.value.dimensions;
      this.restrictAspect = this.activeAspect.restrict;
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              this.targetFile = newFile;
              let targetName = path.basename(newFile.name);
              targetName = `${this.prefix}${_.trim(targetName)}`;
              this.targetName = _.replace(targetName, /\s+/g, '_');
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.$emit('set-cropper', base64File, this.type);
              // clean up the file name for s3
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `${this.prefix}${_.trim(fileName)}`;
              this.localFile = _.replace(fileName, /\s+/g, '_');
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'item openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    destroyCropper() {
      this.$emit('set-cropper', false, false);
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    setCropper() {
      if (this.activeAspect.ratio !== 0) {
        const image = this.$refs.imageCropper;
        this.cropper = new Cropper(image, {
          aspectRatio: this.activeAspect.ratio,
          dragMode: 'move',
          autoCropArea: 1,
          zoomable: false,
        });
      }
    },
    setAspect(aspect) {
      this.activeAspect = aspect;
      if (this.cropper) {
        this.cropper.destroy();
      }
      this.cropper = null;
      this.setCropper();
    },
    async uploadCropped() {
      try {
        let file = this.targetFile;
        let name = this.targetName;
        if (this.activeAspect.ratio !== 0) {
          let { width, height } = this.cropper.getContainerData();
          if (this.activeAspect.width) {
            width = this.activeAspect.width;
          }

          if (this.activeAspect.height) {
            height = this.activeAspect.height;
          }
          const canvas = this.cropper.getCroppedCanvas({ width, height, imageSmoothingQuality: 'high' });
          file = await this.getBlob(canvas);
          name = this.localFile;
        }
        // send off to our s3 upload mixin
        const filePath = `/${this.bucketPath}${name}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: file,
          thumbnail: {
            width: 35,
            srcFile: file,
            name,
            bucketPath: `${this._sitePath}/${this.thumbnails}`,
          },
        });
        this.$store.dispatch('setDropletVal', {
          drp: this.activeItem,
          key: this.type,
          val: filePath,
        });
        this.destroyCropper();
        this.targetFile = null;
        this.targetName = '';
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'item uploadCropped',
          });
        }
      } finally {
        if (this.showFiles) {
          setTimeout(() => {
            this.fetchImageFiles();
          }, 500);
        }
      }
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    async fetchImageFiles() {
      try {
        this.imgLoading = true;
        const assetsFolder = `/_droplet-assets/${this.activeSiteType}/${this.type}`;
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        this.imageFiles = files.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'item fetchImageFiles',
        });
      } finally {
        this.imgLoading = false;
      }
    },
    async deleteImg() {
      this.confirmDeleteImg = false;
      // prep notifcation type
      const notif = {
        id: this.guid(),
        time: 2000,
      };
      const file = path.basename(this._getFromThumb(this.activeImage));
      const thumb = path.basename(this.activeImage);
      try {
        this.imgLoading = true;
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted file: ${path.basename(this.activeImage)}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'item deleteImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.imgLoading = false;
        this.fetchImageFiles();
      }
      // if deleting image that is being used, reset it to default
      if (this.activeItem[this.type] === `/${this.bucketPath}${file}`) {
        this.setImg(`${process.env.S3_ASSETS_URL}mock-images/default-images/no-image-default.svg`, true);
      }
    },
    async setImg(f, def) {
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      this.$store.dispatch('setDropletVal', {
        drp: this.activeItem,
        key: this.type,
        val: file,
      });
    },
    matchImg(file) {
      const f = this._getFromThumb(path.basename(file));
      const media = path.basename(this.activeItem[this.type]);
      return f === media;
    },
    confirmDelete(file) {
      this.activeImage = file;
      this.confirmDeleteImg = true;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    startCase(text) {
      return _.startCase(text);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/edit-item/inputs/upload/index.js